import { format } from 'date-fns'
import { StorageKeys } from '../types/StorageKeys'
import { quizGenerator } from './quizGenerator'

export const registerUserDataSession = userData => {
  const now = new Date()

  const apiUserDataSession = {
    value: userData,
    expiry: now.getTime() + 3600000 // Expiry session after 1 hour
  }

  localStorage.setItem(
    StorageKeys.API_USER_DATA,
    JSON.stringify(apiUserDataSession)
  )
}

export const normalizeDischargeCarePlanData = dischargeCarePlanData => {
  const {
    medications,
    follow_ups,
    personal_caregiver_name,
    medical_conditions,
    medical_conditions_other,
    upcoming_lab_tests,
    completed_lab_tests,
    medical_equipments,
    appointments_upcoming_lab_tests
  } = dischargeCarePlanData

  const medicationsFormatted = medications?.map(med =>
    med.next_dosage_date
      ? {
          ...med,
          next_dosage_date_raw: med.next_dosage_date, //for calendar formatting
          next_dosage_date: format(
            new Date(new Date(med.next_dosage_date).toISOString().slice(0, -1)),
            'EEEE, MMMM d'
          )
        }
      : med
  )

  const newOrChangedMedications = medicationsFormatted.filter(
    med => med.is_change_in_dosage_or_frequency || med.is_new_medicine
  )

  const unchangedOrStoppedMedications = medicationsFormatted.filter(
    med =>
      (!med.is_change_in_dosage_or_frequency && !med.is_new_medicine) ||
      med.is_stopped
  )

  const unchangedMedications = medicationsFormatted.filter(
    med =>
      !med.is_change_in_dosage_or_frequency &&
      !med.is_new_medicine &&
      !med.is_stopped
  )

  // Medications quiz needs to be done over newOrChangedMedications or unchangedMedications since these ones clearly have a dosage assigned
  const quizMedications = newOrChangedMedications.length
    ? newOrChangedMedications
    : unchangedMedications

  const upcomingLabTests = upcoming_lab_tests?.map(upcomingLabTest =>
    upcomingLabTest.date_of_test
      ? {
          ...upcomingLabTest,
          date_raw: upcomingLabTest.date_of_test, //for calendar formatting
          date_of_appointment: format(
            new Date(
              new Date(upcomingLabTest.date_of_test).toISOString().slice(0, -1)
            ),
            'EEEE, MMMM d'
          )
        }
      : upcomingLabTest
  )

  const appointmentsUpcomingLabTests = appointments_upcoming_lab_tests
    ?.filter(apptUpcomingLabTest => apptUpcomingLabTest.date_of_test)
    .map(apptUpcomingLabTest => ({
      ...apptUpcomingLabTest,
      date_raw: apptUpcomingLabTest.date_of_test,
      date_of_appointment: format(
        new Date(
          new Date(apptUpcomingLabTest.date_of_test).toISOString().slice(0, -1)
        ),
        'EEEE, MMMM d'
      )
    }))

  const followUps = follow_ups.map(followUp =>
    followUp.date_of_appointment
      ? {
          ...followUp,
          date_raw: followUp.date_of_appointment, //for calendar formatting
          date_of_appointment: format(
            new Date(
              new Date(followUp.date_of_appointment).toISOString().slice(0, -1)
            ),
            'EEEE, MMMM d'
          )
        }
      : followUp
  )

  // if the appointment doesn't contain the date_of_appointment
  // means that is an unscheduled appointment
  const appointments = followUps.filter(
    followUp => followUp.date_of_appointment
  )

  const scheduledAppointments = [
    ...appointments,
    ...appointmentsUpcomingLabTests
  ]

  const unscheduleFollowUps = followUps.filter(
    followUp => !followUp.date_of_appointment
  )

  const unscheduledLabTestsAppointments = appointments_upcoming_lab_tests?.filter(
    apptUpcomingLabTest => !apptUpcomingLabTest.date_of_test
  )

  const unscheduledAppointments = [
    ...unscheduleFollowUps,
    ...unscheduledLabTestsAppointments
  ]

  const symptomsList = medical_conditions[0]?.medical_condition?.education_content?.find(
    item => item.shortDescription === 'Symptom List'
  )

  const quizData = quizGenerator(quizMedications, scheduledAppointments)

  // To refactor: give another name to this object to signify that provides more information
  // than about questions that needs to be skipped
  const skippedData = {
    'question#24040': {
      // Caregiver
      response: '60947',
      label: `Okay, I'll ask them to listen`,
      shouldSkip: !personal_caregiver_name
    },
    'question#24263': {
      // Co-morbidities list
      response: '61435',
      label: `Okay`,
      shouldSkip: medical_conditions_other?.length === 0
    },
    'question#24111': {
      // New or changed medications (1st screen)
      response: '61092',
      label: `Let's skip to another section, I understand my medicines`,
      shouldSkip: medications?.length === 0
    },
    'question#24266': {
      // Medications quiz intro
      response: '61440',
      label: `No, skip to the next section`,
      shouldSkip: medications?.length === 0
    },
    'question#24116': {
      // New or changed medications (2nd screen)
      response: '61102',
      label: `Okay, I'm ready`,
      shouldSkip: newOrChangedMedications?.length === 0
    },
    'question#24117': {
      // New or changed medications (3rd screen)
      response: unchangedOrStoppedMedications?.length === 0 ? '61104' : '61103',
      label:
        unchangedOrStoppedMedications?.length === 0
          ? `No, let's move on`
          : `Yes, I'd like to hear about my existing medicines that have not changed`,
      specialButtons: true,
      shouldSkip: newOrChangedMedications?.length === 0
    },
    'question#24118': {
      // Unchanged medications
      response: '61105',
      label: `Okay`,
      specialButtons: true,
      shouldSkip: unchangedOrStoppedMedications?.length === 0
    },
    'question#23727': {
      // Scheduled appointments (1st question)
      response: '60290',
      label: `No, let's skip to another topic`,
      shouldSkip: scheduledAppointments?.length === 0
    },
    'question#24053': {
      // Scheduled appointments
      response: '60974',
      label: `Okay`,
      shouldSkip: scheduledAppointments?.length === 0
    },
    'question#24054': {
      // Unscheduled appointments
      response: '60975',
      label: `Okay`,
      shouldSkip: unscheduledAppointments?.length === 0
    },
    'question#24055': {
      // Scheduled appointments quiz intro
      response: '60977',
      label: `Okay`,
      shouldSkip: scheduledAppointments?.length === 0
    },
    'question#23734': {
      // Scheduled appointments issue?
      response: '60302',
      label: `No, everything looks fine`,
      shouldSkip: scheduledAppointments?.length === 0
    },
    'question#24196': {
      // Upcoming tests
      response: '61280',
      label: `No, let's move on`,
      shouldSkip: upcoming_lab_tests?.length === 0
    },
    'question#24200': {
      // Pending tests
      response: '61286',
      label: `No, I understand my pending tests`,
      shouldSkip: completed_lab_tests?.length === 0
    },
    'question#24352': {
      // Medical equipment
      response: '61627',
      label: `No, let's finish`,
      shouldSkip: medical_equipments?.length === 0
    },
    'question#24150': {
      // Symptoms list
      response: '61186',
      label: `Okay`,
      shouldSkip: !symptomsList
    }
  }

  return {
    ...dischargeCarePlanData,
    follow_ups: followUps,
    upcoming_lab_tests: upcomingLabTests,
    symptoms_list: symptomsList,
    medications: medicationsFormatted,
    scheduled_appointments: scheduledAppointments,
    scheduled_follow_ups: appointments,
    scheduled_upcoming_lab_tests: appointmentsUpcomingLabTests,
    unscheduled_appointments: unscheduledAppointments,
    unscheduled_upcoming_lab_tests: unscheduledLabTestsAppointments,
    unscheduled_follow_ups: unscheduleFollowUps,
    firstInstanceMedications: newOrChangedMedications,
    secondInstanceMedications: unchangedOrStoppedMedications,
    skippedData: skippedData,
    quiz_data: quizData
  }
}

export const registerDischargeCarePlanSession = (
  dischargeCarePlanData,
  patientId
) => {
  const now = new Date()

  const apiDischargeCarePlanSession = {
    value: dischargeCarePlanData,
    expiry: now.getTime() + 3600000 // Expiry session after 1 hour
  }

  localStorage.setItem(
    StorageKeys.API_DISCHARGE_CARE_PLAN_DATA,
    JSON.stringify({ ...apiDischargeCarePlanSession, patientId })
  )
}
